'use client'

import { DUR, EASE } from '@/animations/settings/vars'
import { calculateDistance, useGSAPMedia } from '@/hooks'
import { cn } from '@/lib/utils'
import { useStoreAccomodation, useStoreGeo, useStoreI18n } from '@/store'
import {
  Typo3AccomodationItem,
  Typo3CultureItem,
  Typo3EatDrinkItem,
  Typo3FreetimeItem,
  Typo3NatureItem,
  Typo3ShoppingItem,
  Typo3SportItem,
  Typo3SportServicesItem,
  Typo3TourItem
} from '@/types'
import { Typo3EventItem } from '@/types/typo3/sections/spotsanity-eventlist'
import {
  BaseLink,
  Div,
  Icon,
  Img,
  OpeningStatus,
  RatingStars
} from '@/ui/components'
import { gsap } from '@gsap'
import { COLORS, MEDIA } from '@spot'
import { memo, useEffect, useRef } from 'react'
import { useHover } from 'usehooks-ts'
import { RatingStarsProps } from '../RatingStars/RatingStars'
import { BaseLinkProps } from '../types'

type DataType =
  | Typo3EventItem
  | Typo3AccomodationItem
  | Typo3EatDrinkItem
  | Typo3NatureItem
  | Typo3ShoppingItem
  | Typo3CultureItem
  | Typo3FreetimeItem
  | Typo3SportItem
  | Typo3TourItem
  | Typo3SportServicesItem

interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'content'> {
  variant: 'hotel' | 'event' | 'tour' | 'download' | 'default' | 'restaurant'
  isImage: boolean
  linkProps?: BaseLinkProps
  isTopEvent?: boolean
  isBadge?: boolean
  isCoordinates?: boolean
  data: DataType
}

const CardHorizontalNew = ({
  variant,
  isImage,
  linkProps,
  isBadge,
  isCoordinates,
  data,
  ...props
}: Props) => {
  const imageRef = useRef<any>(null)
  const ref = useRef<any>(null)
  const tl = useRef<GSAPTimeline | null>(null)

  //stores
  const D = useStoreI18n.use.dictionary()
  const currentLocation = useStoreGeo.use.location()
  const roomsData = useStoreAccomodation.use.roomsData()

  //Hooks
  const imageHover = useHover(imageRef)
  const cardHover = useHover(ref)

  //animation
  useGSAPMedia({
    media: MEDIA.desktop,
    scope: ref,
    callback(_c) {
      const image = '[data-image] img'
      tl.current = gsap.timeline({
        paused: true,
        defaults: { ease: EASE.out, duration: DUR.default }
      })
      isImage && tl.current.to(image, { scale: 1.2 }, 0)
      tl.current.to(ref.current, { color: COLORS?.alphaBlack300 }, 0)
    }
  })

  useEffect(() => {
    if (tl.current) {
      isImage
        ? imageHover
          ? tl.current.play()
          : tl.current.reverse()
        : cardHover
          ? tl.current.play()
          : tl.current.reverse()
    }
  }, [imageHover, cardHover, isImage])

  //composer
  const classes = cn(
    'relative grid grid-cols-[auto_1fr] items-center border-b border-b-neutral-300',
    !isImage && 'min-h-[10rem]',
    variant === 'tour' && '[&_.content]:max-md:pt-[3.25rem]',
    'max-sm:grid-cols-1 max-sm:p-md'
  )

  const linkHref =
    variant !== 'tour'
      ? ('slug' in data && data?.slug) || ''
      : ('link' in data && data?.link?.href) || ''

  return (
    <BaseLink
      href={linkHref}
      isTransition={variant !== 'tour'}
      nextLink={variant !== 'tour'}
      target={variant === 'tour' ? '_blank' : undefined}
      aria-label="link"
      {...linkProps}
      ref={ref}
    >
      <Div className={classes} {...props}>
        {isImage && data?.images && !!data?.images?.length && (
          <div className="p-md pl-md pb-md pt-md max-sm:p-0" ref={imageRef}>
            <Img
              unoptimized
              crop={{ width: 600, height: 600 }}
              data-image
              data={data.images[0]}
              fill
              objectFit
              className="h-[12.875rem] w-[11.5rem] max-sm:h-auto max-sm:w-full max-sm:pt-[90%] md:h-[13.5rem] md:w-[13.6875rem]"
              sizes={`${MEDIA.mobile} 20rem, 18rem`}
              quality={90}
            />
          </div>
        )}
        {variant === 'event' && 'isTopEvent' in data && data?.isTopEvent && (
          <div className="gap-x-xs bg-primary-300 p-xs px-sm absolute top-0 right-0 flex items-center justify-start text-neutral-100! max-sm:relative max-sm:justify-self-start">
            <Icon className="h-6 w-6" icon="material-symbols:star" />
            <p className="text-btn-small">Top Event</p>
          </div>
        )}
        {(variant === 'default' ||
          variant === 'hotel' ||
          variant === 'restaurant') &&
          isBadge &&
          'openings' in data && (
            <OpeningStatus
              //itemName={data?.title}
              data={data?.openings}
              className={cn(
                'max-sm:relative! max-sm:justify-self-start',
                !isImage && 'max-sm:mt-[calc(-1*var(--spacing-md))]'
              )}
            />
          )}
        {/* {variant === 'tour' && (
          <TourBadge
            data={data}
            className={cn(
              'max-sm:relative! max-sm:justify-self-start',
              !isImage && 'max-sm:mt-[calc(-1*var(--spacing-md))]'
            )}
          />
        )} */}
        <div className="content grid-rows-auto gap-y-md p-lg max-sm:mt-lg grid auto-rows-auto grid-cols-1 place-items-start self-center max-sm:p-0">
          {'textDates' in data &&
            data?.textDates &&
            !!data.textDates.length && (
              <p className="line-clamp-3 text-neutral-400 md:line-clamp-2">
                {data.textDates[0].isInterval
                  ? data.textDates[0].interval
                  : data.textDates[0].firstActive}
              </p>
            )}
          <h3 className={cn('h3', 'md:line-clamp-2 md:max-w-[90%]')}>
            {data?.title}
          </h3>
          <div>
            {'city' in data && data?.city && <p>{data.city}</p>}
            {variant === 'event' &&
              'infoLocation' in data &&
              data?.infoLocation && <p>{data.infoLocation}</p>}

            {data.summary && (
              <p
                className="line-clamp-3 text-neutral-400 md:line-clamp-2"
                dangerouslySetInnerHTML={{
                  __html: data?.summary as string
                }}
              />
            )}
          </div>
          <div className="gap-x-md flex flex-wrap items-center">
            {/* {variant === 'tour' && <AdditionalInfo data={data} />} */}
            {variant === 'hotel' &&
              'stars' in data &&
              data?.stars &&
              data.stars !== 'stars-0' && (
                <>
                  <RatingStars
                    stars={
                      data?.isSuperior
                        ? 6
                        : (data.stars.replace(
                            'stars-',
                            ''
                          ) as RatingStarsProps['stars'])
                    }
                  />
                </>
              )}
            {variant === 'hotel' &&
              'stcId' in data &&
              roomsData &&
              !!roomsData.length &&
              roomsData
                .filter((room) => room.stc_id === data.stcId)
                .map((room, index) => (
                  <>
                    {data.stars && data.stars !== 'stars-0' && <div>·</div>}
                    <div
                      className="font-weight-700"
                      key={index?.toString()}
                    >{`${D['fe.booking.priceFrom']} CHF ${room?.price?.toFixed(2)}`}</div>
                  </>
                ))}
            {isCoordinates &&
              data.coordinates?.hasCoordinates &&
              data.coordinates.latitude &&
              data.coordinates.longitude &&
              currentLocation.latitude !== 0 &&
              currentLocation.longitude !== 0 && (
                <>
                  {(variant === 'hotel' && roomsData && !!roomsData.length) ||
                    ('stars' in data &&
                      data.stars &&
                      data.stars !== 'stars-0' && <div>·</div>)}
                  <div>
                    {`${calculateDistance(
                      {
                        latitude: currentLocation.latitude,
                        longitude: currentLocation.longitude
                      },
                      {
                        latitude: +data.coordinates.latitude,
                        longitude: +data.coordinates.longitude
                      }
                    ).toFixed(1)} km`}
                  </div>
                </>
              )}
          </div>
        </div>
      </Div>
    </BaseLink>
  )
}

export default memo(CardHorizontalNew)
